/**
 * AffDice SuperAdmin — entry point.
 *
 * §3.7's session rides an `HttpOnly` cookie the browser manages — never
 * `localStorage`, still, since anything script-readable is readable by an
 * injected script too. The cookie is what makes a reload not sign somebody
 * out; `useExistingSession` below is the boot-time check for one, using a
 * client built with no token, since the whole point is asking whether the
 * cookie alone is already enough.
 */

import { useEffect, StrictMode, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Callout,
  ChangePassword,
  EnrolMfa,
  Loading,
  NotConfigured,
  SignIn,
  ToastProvider,
  explainFailure,
  onboardingApi,
  postLogin,
  preferredShippedLocale,
  setDisplayLocale,
  useExistingSession,
  useLoad,
  useSessionExpired,
} from '@affdice/portal-kit';

import '@affdice/portal-kit/styles.css';

import { App } from './App.tsx';
import { PlatformApi } from './client.ts';
import { t } from './messages.ts';

const baseUrl = import.meta.env['VITE_API_BASE'];

/*
 * What to read this console in: the browser's own preference, when it is one
 * of the languages the console ships (en, es, pt-BR), else English. Set
 * before anything renders, so the sign-in form already reads in it.
 */
setDisplayLocale(preferredShippedLocale(navigator.languages));

function Root() {
  if (!baseUrl) return <NotConfigured portal="the KD Affiliate SuperAdmin" />;
  return <RootBooted baseUrl={baseUrl} />;
}

function RootBooted({ baseUrl }: { baseUrl: string }) {
  const bootApi = useMemo(
    () => new PlatformApi({ baseUrl, token: () => undefined }),
    [baseUrl],
  );

  const existing = useExistingSession(bootApi);

  const [session, setSession] = useState<{
    token?: string;
    userId?: string;
    permissions: readonly string[];
  } | null>(null);
  // Distinct from `session === null`, which is also true before the boot
  // check has answered — without this, that first render would show the
  // sign-in form for a moment even when a cookie is about to restore one.
  const [booted, setBooted] = useState(false);
  // A failed boot probe must not trap the user on a dead-end page — they
  // can still try to sign in (and the warning explains why the probe failed).
  const [ignoreUnreachable, setIgnoreUnreachable] = useState(false);
  // A session that ends mid-use goes straight back to the sign-in form, rather
  // than leaving the screen it ended on to say so.
  useSessionExpired(() => setSession(null));

  useEffect(() => {
    if (existing.state === 'checking') return;
    if (existing.state === 'found') setSession({ permissions: [] });
    setBooted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.state]);

  // §3.7 — the two steps between being given an account and having one. Both
  // still need the credentials, and there is no session yet to carry them.
  const [onboarding, setOnboarding] = useState<
    { step: 'enrol' | 'password'; email: string; password: string } | null
  >(null);
  const onboard = onboardingApi(baseUrl);

  if (!booted) return <Loading />;

  /*
   * The diagnosis is for whoever can act on it.
   *
   * "Confirm api-core is on port 3001" is a sentence for somebody running the
   * stack on their own machine. On a deployed console it reaches a reader who
   * has no port to check, in place of the sign-in form they came for — so in a
   * build that is not the dev server, an unreachable API falls through to
   * sign-in, which says plainly enough that it cannot get in. An expired
   * session already lands there on its own: a 401 is read as "no session", not
   * as an outage.
   */
  if (import.meta.env.DEV && existing.state === 'unreachable' && !ignoreUnreachable && !session) {
    const shown = explainFailure(existing.error);
    return (
      <div className="main signin">
        <div className="page-head"><h1>KD Affiliate SuperAdmin</h1></div>
        <Callout tone="error">{shown.message}</Callout>
        <Callout>
          {t('shell.unreachable', {
            base: baseUrl,
            origin: typeof location !== 'undefined' ? location.origin : '',
          })}
        </Callout>
        <div className="form-actions">
          <button className="primary" type="button" onClick={() => location.reload()}>
            {t('shell.retry')}
          </button>
          <button className="link-button" type="button" onClick={() => setIgnoreUnreachable(true)}>
            {t('shell.continueToSignIn')}
          </button>
        </div>
      </div>
    );
  }

  if (onboarding?.step === 'enrol') {
    return (
      <EnrolMfa
        email={onboarding.email}
        password={onboarding.password}
        api={onboard}
        onDone={() => setOnboarding(null)}
      />
    );
  }

  if (onboarding?.step === 'password') {
    return (
      <ChangePassword
        email={onboarding.email}
        currentPassword={onboarding.password}
        api={onboard}
        onDone={() => setOnboarding(null)}
      />
    );
  }

  if (!session) {
    return (
      <SignIn
        title="KD Affiliate SuperAdmin"
        post={(body) => postLogin(baseUrl, { ...body, portal: 'platform' })}
        onSignedIn={(token, permissions, userId) => setSession({ token, userId, permissions })}
        onNeedsEnrolment={(email, password) => setOnboarding({ step: 'enrol', email, password })}
        onMustChangePassword={(email, password) =>
          setOnboarding({ step: 'password', email, password })
        }
      />
    );
  }

  return (
    <SignedIn
      api={new PlatformApi({ baseUrl, token: () => session.token })}
      session={session}
      onSignedOut={() => setSession(null)}
    />
  );
}

/**
 * §3.6 — permissions are re-read on every load.
 *
 * Trusting the set handed back at sign-in means a role revoked mid-session
 * keeps working until the person happens to sign in again, and §3.6's
 * time-boxed elevation is exactly the case where that matters: the elevation
 * expires and the console does not notice.
 */
function SignedIn({
  api,
  session,
  onSignedOut,
}: {
  api: PlatformApi;
  session: { token?: string; userId?: string; permissions: readonly string[] };
  onSignedOut: () => void;
}) {
  const fresh = useLoad(() => api.permissions(), []);
  const permissions =
    fresh.state === 'ready' ? fresh.value.permissions : session.permissions;
  /*
   * Who this is. `session.userId` only exists when this tab did the signing
   * in — a reload restores the session from the cookie, and "Login as" opens
   * a tab with a token and nothing else, so in both of those it was missing
   * and screens that ask "is this row me?" got no answer. The permissions
   * refresh runs on every load and now carries the id, so it is the answer
   * that always arrives.
   */
  const userId = session.userId ?? (fresh.state === 'ready' ? fresh.value.user_id : undefined);

  return (
    <App
      api={api}
      permissions={permissions}
      onboard={onboardingApi(baseUrl)}
      {...(userId ? { userId } : {})}
      onSignedOut={onSignedOut}
    />
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ToastProvider>
      <Root />
    </ToastProvider>
  </StrictMode>,
);
